/**
 * Die **eine** Wortzählung des Orientierungstextes.
 *
 * Der Text einer global bewerteten Aufgabe wird an drei Stellen in Wörter zerlegt, und alle
 * drei müssen dasselbe zählen — sonst sitzen die Wortmarken am Classic-Server auf anderen Wörtern
 * als am Gerät:
 *
 * 1. **Download** (`server/classic/abbildung.ts`): merkt sich je Antwortzeile ihre Wortzahl.
 * 2. **Anzeige** (`features/bogen/TaskCard.tsx::GlobalBody`): vergibt die Indizes, unter denen
 *    die Marken in `pickedWords` stehen — laufend über den ganzen Block.
 * 3. **Rückweg** (`server/classic/kennung.ts::verteileWortindexe`): verteilt diese Indizes
 *    wieder auf die Classic-Antwortzeilen.
 *
 * Bis zum 2026-09-13 hatte jede Stelle ihre eigene Zerlegung. Sie stimmten nur überein,
 * solange der Text schon zusammengezogen war (`abbildung.ts::alsText` tut das): die Anzeige
 * trennte an **einem** Leerzeichen, der Download an Leerraum beliebiger Länge. Zwei
 * Leerzeichen hintereinander hätten gereicht — die Anzeige hätte ein leeres Wort mitgezählt
 * und wäre von da an um eins verschoben gewesen.
 *
 * Deshalb steht die Zerlegung jetzt hier, und alle drei Stellen rufen sie auf.
 */

/**
 * Ein Absatz in seine Wörter.
 *
 * Leerraum beliebiger Art und Länge trennt; vorn und hinten wird abgeschnitten. Ein Absatz
 * ohne Inhalt hat **keine** Wörter — kein leeres Wort, das die Anzeige als unsichtbare
 * Antippfläche zeigte und die Zählung verschöbe.
 */
export function woerterEinesAbsatzes(absatz: string): string[] {
  const sauber = absatz.trim();
  return sauber === '' ? [] : sauber.split(/\s+/);
}

/** Wie viele Wörter ein Text beisteuert. */
export function woerterIn(text: string): number {
  return woerterEinesAbsatzes(text).length;
}

/**
 * Der ganze Block: seine Absätze, jeder in seinen Wörtern.
 *
 * Die Absätze trennt `\n` — so setzt der Download die Texte mehrerer Antwortzeilen zusammen
 * (`abbildung.ts::blockAusZeilen`). Der Wortindex läuft über den **ganzen** Block weiter, die
 * Anzeige addiert dafür die Längen auf.
 */
export function absaetzeMitWoertern(block: string): string[][] {
  return block.split('\n').map(woerterEinesAbsatzes);
}
